import { useEffect, useMemo, useRef, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Plus, X, Link2, Link2Off, Save, Search } from "lucide-react";

/**
 * Panel admin — Tiendas Aliadas.
 *
 * CRUD completo de la vitrina que sale en el home. El admin puede:
 * - Subir logo (usa `/api/files/upload` scope=public, kind=misc).
 * - Vincular la tienda a un vendedor `account_type=store` aprobado
 *   (autocomplete server-side).
 * - Dejar sin vincular → la tarjeta sale como "Próximamente" en el home.
 * - Ordenar (input numérico), activar/desactivar (toggle rápido), borrar.
 * - Editar la URL + label del CTA "Quiero ser tienda aliada".
 */
function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

function StoreLookup({ value, onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/stores-lookup", { params: { q } });
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [q, open]);

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm rounded-md px-3 py-2 flex items-center gap-2">
          <Link2 size={14} />
          <span className="truncate">{value.display_name}</span>
        </div>
        <button
          type="button"
          onClick={onClear}
          data-testid="partner-lookup-clear"
          className="text-neutral-400 hover:text-red-400 transition-colors p-2"
          title="Desvincular"
        >
          <Link2Off size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input
          data-testid="partner-lookup-input"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar tienda por nombre o RUC…"
          className="bg-[#0A0A0A] border-[#262626] pl-9"
        />
      </div>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#262626] rounded-md max-h-64 overflow-auto shadow-xl">
          {loading && <div className="px-3 py-2 text-xs text-neutral-500">Buscando…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-neutral-500">
              {q ? "Sin resultados. La tienda debe ser cuenta tipo 'store' y estar aprobada." : "Escribe para buscar tiendas verificadas."}
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              data-testid={`partner-lookup-option-${r.id}`}
              onClick={() => {
                onSelect(r);
                setOpen(false);
                setQ("");
              }}
              className="block w-full text-left px-3 py-2 text-sm text-neutral-200 hover:bg-[#171717]"
            >
              <div className="font-medium">{r.display_name}</div>
              {r.store_name && r.name && r.store_name !== r.name && (
                <div className="text-[10px] text-neutral-500">Titular: {r.name}</div>
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full text-center text-[10px] text-neutral-500 hover:text-neutral-300 border-t border-[#262626] py-1.5"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

function StoreFormModal({ store, onClose, onSaved }) {
  const isEdit = !!store?.id;
  const [name, setName] = useState(store?.name || "");
  const [logoPath, setLogoPath] = useState(store?.logo_path || "");
  const [logoUrl, setLogoUrl] = useState(store?.logo_url || null);
  const [sortOrder, setSortOrder] = useState(store?.sort_order ?? 0);
  const [isActive, setIsActive] = useState(store?.is_active ?? true);
  const [seller, setSeller] = useState(
    store?.seller_id
      ? { id: store.seller_id, display_name: store.seller_display_name || "(vendedor)" }
      : null
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El logo debe pesar menos de 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", "public");
      fd.append("kind", "misc");
      const r = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoPath(r.data.path);
      setLogoUrl(`/api/files/public/${r.data.path}`);
      toast.success("Logo subido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir el logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error("El nombre debe tener al menos 2 caracteres");
      return;
    }
    if (!logoPath) {
      toast.error("Sube un logo antes de guardar");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          name: name.trim(),
          logo_path: logoPath,
          sort_order: parseInt(sortOrder, 10) || 0,
          is_active: isActive,
        };
        // Manejo tri-estado del vínculo: si el estado actual (`seller`) es
        // null y antes había uno, mandamos `clear_seller=true` para
        // desvincular explícitamente. Si seleccionamos uno nuevo, mandamos
        // `seller_id`.
        if (seller) body.seller_id = seller.id;
        else if (store?.seller_id) body.clear_seller = true;
        await api.put(`/admin/partner-stores/${store.id}`, body);
        toast.success("Tienda actualizada");
      } else {
        await api.post("/admin/partner-stores", {
          name: name.trim(),
          logo_path: logoPath,
          seller_id: seller?.id || null,
          sort_order: parseInt(sortOrder, 10) || 0,
          is_active: isActive,
        });
        toast.success("Tienda aliada creada");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="partner-store-modal"
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-[#262626] rounded-md w-full max-w-lg max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between border-b border-[#262626] px-5 py-4">
          <h3 className="font-display text-xl">{isEdit ? "Editar tienda aliada" : "Nueva tienda aliada"}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
            data-testid="partner-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider">Nombre a mostrar</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fenix Cards, Kabuki TCG, …"
              maxLength={60}
              className="bg-[#111] border-[#262626] mt-1.5"
              data-testid="partner-store-name-input"
            />
            <div className="text-[10px] text-neutral-500 mt-1">Cómo aparecerá debajo del logo en el home ({name.length}/60).</div>
          </div>

          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider">Logo</Label>
            <div className="mt-1.5 flex items-start gap-3">
              <div className="w-24 h-24 bg-[#111] border border-[#262626] rounded-md flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl ? (
                  <img src={resolveUrl(logoUrl)} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[10px] text-neutral-600 text-center px-2">Sin logo</span>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="partner-store-logo-file"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="partner-store-logo-upload-btn"
                  className="inline-flex items-center gap-2 bg-[#171717] hover:bg-[#1F1F1F] border border-[#262626] rounded-md px-3 py-2 text-sm"
                >
                  <BusyLabel busy={uploading} text="Subiendo…"><Upload size={14} /> {logoUrl ? "Cambiar logo" : "Subir logo"}</BusyLabel>
                </button>
                <div className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
                  JPG, PNG, WEBP o HEIC · máx 5MB · idealmente cuadrado, fondo transparente u oscuro.
                </div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider">Vincular a catálogo</Label>
            <div className="mt-1.5">
              <StoreLookup
                value={seller}
                onSelect={(u) => setSeller(u)}
                onClear={() => setSeller(null)}
              />
            </div>
            <div className="text-[10px] text-neutral-500 mt-1 leading-relaxed">
              Si la tienda todavía no tiene su catálogo en HitTCG, déjalo vacío. La tarjeta aparecerá con badge <b className="text-neutral-400">Próximamente</b> y no será clickeable hasta que la vincules.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-neutral-400 uppercase tracking-wider">Orden</Label>
              <Input
                type="number"
                min={0}
                max={9999}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-[#111] border-[#262626] mt-1.5"
                data-testid="partner-store-sort-input"
              />
              <div className="text-[10px] text-neutral-500 mt-1">Menor = más arriba en el home.</div>
            </div>
            <div>
              <Label className="text-xs text-neutral-400 uppercase tracking-wider">Estado</Label>
              <button
                type="button"
                onClick={() => setIsActive((v) => !v)}
                data-testid="partner-store-active-toggle"
                className={`w-full mt-1.5 h-10 rounded-md border text-sm font-medium transition ${isActive ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-[#111] border-[#262626] text-neutral-400"}`}
              >
                {isActive ? "Activo · visible en home" : "Inactivo · oculto"}
              </button>
            </div>
          </div>
        </div>
        <div className="border-t border-[#262626] px-5 py-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-white"
          >
            Cancelar
          </button>
          <Button
            type="submit"
            disabled={saving || uploading}
            data-testid="partner-store-save-btn"
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
          >
            <Save size={14} className="mr-2" />
            <BusyLabel busy={saving} text="Guardando…">{isEdit ? "Guardar cambios" : "Crear tienda"}</BusyLabel>
          </Button>
        </div>
      </form>
    </div>
  );
}

function CtaSettingsCard({ settings, onSaved }) {
  const [url, setUrl] = useState(settings?.cta_url || "/register");
  const [label, setLabel] = useState(settings?.cta_label || "Quiero ser tienda aliada");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUrl(settings?.cta_url || "/register");
    setLabel(settings?.cta_label || "Quiero ser tienda aliada");
  }, [settings]);

  const save = async () => {
    if (!url.trim() || !label.trim()) {
      toast.error("URL y texto del botón son requeridos");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/partner-stores-settings", {
        cta_url: url.trim(),
        cta_label: label.trim(),
      });
      toast.success("Botón actualizado");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-4 mb-4">
      <div className="text-xs text-neutral-400 uppercase tracking-wider mb-3">Botón "Quiero ser tienda aliada"</div>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">URL destino</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/register o mailto:contacto@hittcg.store"
            className="bg-[#0A0A0A] border-[#262626] mt-1"
            data-testid="partner-cta-url-input"
          />
        </div>
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">Texto del botón</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={60}
            className="bg-[#0A0A0A] border-[#262626] mt-1"
            data-testid="partner-cta-label-input"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="partner-cta-save-btn"
          className="bg-[#EF4444] hover:bg-[#DC2626] text-white h-10"
        >
          <BusyLabel busy={saving} text="Guardando…">Guardar</BusyLabel>
        </Button>
      </div>
      <div className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
        Acepta rutas internas (<code className="text-neutral-400">/register</code>) o URLs completas / mailto / tel.
      </div>
    </div>
  );
}

export default function AdminPartnerStores() {
  const [list, setList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} para nueva | store obj para editar

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/partner-stores");
      setList(r.data?.stores || []);
      setSettings(r.data?.settings || null);
    } catch (err) {
      toast.error("No se pudieron cargar las tiendas aliadas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/partner-stores/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (err) {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.name}" de tiendas aliadas? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/partner-stores/${s.id}`);
      toast.success("Tienda eliminada");
      load();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const counts = useMemo(() => ({
    total: list.length,
    active: list.filter((s) => s.is_active).length,
    withCatalog: list.filter((s) => s.is_active && s.seller_verified).length,
    comingSoon: list.filter((s) => s.is_active && !s.seller_verified).length,
  }), [list]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl">Tiendas aliadas</h2>
          <p className="text-xs text-neutral-500 mt-1">
            Gestiona la vitrina que aparece en el home. Las inactivas quedan ocultas al público.
          </p>
        </div>
        <Button
          onClick={() => setEditing({})}
          data-testid="partner-store-new-btn"
          className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
        >
          <Plus size={14} className="mr-2" /> Nueva tienda aliada
        </Button>
      </div>

      <CtaSettingsCard settings={settings} onSaved={load} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-[#111] border border-[#262626] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total</div>
          <div className="font-display text-2xl mt-1">{counts.total}</div>
        </div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Activas</div>
          <div className="font-display text-2xl text-emerald-400 mt-1">{counts.active}</div>
        </div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Con catálogo</div>
          <div className="font-display text-2xl text-[#EF4444] mt-1">{counts.withCatalog}</div>
        </div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Próximamente</div>
          <div className="font-display text-2xl text-amber-400 mt-1">{counts.comingSoon}</div>
        </div>
      </div>

      {loading && <div className="text-neutral-500 text-sm">Cargando…</div>}

      {!loading && list.length === 0 && (
        <div className="border border-dashed border-[#262626] rounded-md p-10 text-center text-neutral-500">
          Aún no hay tiendas aliadas. Agrega la primera para que aparezca en el home.
        </div>
      )}

      {!loading && list.length > 0 && (
        <div className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0A0A0A] border-b border-[#262626]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3 w-20">Logo</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Catálogo</th>
                <th className="px-4 py-3 w-20 text-center">Orden</th>
                <th className="px-4 py-3 w-32">Estado</th>
                <th className="px-4 py-3 w-32 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="border-t border-[#262626]" data-testid={`admin-partner-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="w-12 h-12 bg-[#0A0A0A] border border-[#262626] rounded flex items-center justify-center overflow-hidden">
                      {s.logo_url ? (
                        <img src={resolveUrl(s.logo_url)} alt="logo" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-[9px] text-neutral-600">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    {s.seller_id ? (
                      s.seller_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                          <Link2 size={12} /> {s.seller_display_name || "vinculado"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400" title="El vendedor vinculado no está aprobado o está baneado. La tarjeta sale como Próximamente en el home.">
                          <Link2 size={12} /> {s.seller_display_name || "vinculado"} · no aprobado
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                        <Link2Off size={12} /> Sin vincular
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-neutral-400">{s.sort_order}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(s)}
                      data-testid={`partner-toggle-active-${s.id}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${s.is_active ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20" : "bg-[#0A0A0A] border-[#262626] text-neutral-500 hover:text-white"}`}
                    >
                      {s.is_active ? "Activa" : "Inactiva"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(s)}
                        data-testid={`partner-edit-${s.id}`}
                        className="text-xs px-2 py-1 rounded text-neutral-400 hover:text-white hover:bg-[#171717]"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(s)}
                        data-testid={`partner-delete-${s.id}`}
                        className="text-neutral-500 hover:text-red-400 p-1"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <StoreFormModal
          store={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
